/**
 * @fileoverview React Hook for Application Version Monitoring
 *
 * This module provides a custom React hook that monitors the deployed application
 * version by periodically checking for updates. It compares the running version
 * with the deployed version to detect when a new build has been released, enabling
 * the application to notify users when updates are available.
 *
 * Key Features:
 * - Periodic version checking via configurable interval (default: 5 minutes)
 * - Automatic comparison between current and deployed versions
 * - Graceful error handling to prevent application disruption
 * - Automatic cleanup on component unmount
 * - Configurable check interval for different environments
 * - No backend changes required - purely frontend solution
 *
 * Version Detection Mechanism:
 * - Current version: Read from REACT_APP_VERSION environment variable at build time
 * - Deployed version: Fetched from /version.json endpoint
 * - Update detected: When deployed version differs from current version
 *
 * Usage Pattern:
 * - Import and use in top-level components (typically App.js)
 * - Hook returns state indicating if update is available
 * - Component can display notification banner based on state
 * - User can refresh page to get new version
 *
 * Error Handling:
 * - Network failures are silently ignored (no user disruption)
 * - Invalid JSON responses are caught and logged
 * - Missing version file falls back gracefully
 * - All errors prevent false-positive update notifications
 *
 * Performance Considerations:
 * - Lightweight HTTP GET request (minimal payload)
 * - Configurable interval prevents excessive requests
 * - Automatic cleanup prevents memory leaks
 * - Runs on background interval (non-blocking)
 *
 * Security Considerations:
 * - Version information is public and non-sensitive
 * - No authentication required for version checks
 * - No CSRF protection needed (read-only operation)
 * - Safe to expose version information publicly
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 * @since 2024-01-01
 *
 * @requires react - For useState and useEffect hooks
 *
 * @example
 * // Basic usage in App component
 * import { useVersionCheck } from './hooks/useVersionCheck';
 *
 * function App() {
 *   const { updateAvailable, newVersion } = useVersionCheck();
 *
 *   return (
 *     <div>
 *       {updateAvailable && (
 *         <UpdateNotificationBanner version={newVersion} />
 *       )}
 *       <AppContent />
 *     </div>
 *   );
 * }
 *
 * @example
 * // With custom check interval (every 2 minutes)
 * const { updateAvailable, newVersion } = useVersionCheck(120000);
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Version check interval in milliseconds
 * Default: 1800000ms = 30 minutes
 *
 * This interval balances responsiveness with server load:
 * - Frequent enough to notify users of updates reasonably quickly
 * - Infrequent enough to avoid excessive HTTP requests
 * - Configurable via parameter for different environments
 *
 * @constant {number}
 */
const DEFAULT_CHECK_INTERVAL = 30000; // 30 seconds

/**
 * Custom React hook for monitoring application version updates
 *
 * Periodically fetches the deployed version from /version.json and compares
 * it with the current running version to detect when a new build is available.
 * Returns state that components can use to display update notifications.
 *
 * Behavior:
 * - Performs initial version check on mount
 * - Sets up interval to check periodically
 * - Updates state when new version is detected
 * - Cleans up interval on unmount
 * - Handles all errors gracefully
 *
 * State Management:
 * - updateAvailable: Boolean indicating if update is available
 * - newVersion: String containing the new version identifier
 *
 * Version Comparison:
 * - Current version from process.env.REACT_APP_VERSION (set at build time)
 * - Deployed version from /version.json endpoint
 * - Versions are considered different if strings don't match exactly
 *
 * @param {number} [checkInterval=DEFAULT_CHECK_INTERVAL] - Interval in milliseconds
 *   between version checks. Defaults to 30 minutes (1800000ms).
 * @param {boolean} [isUserActive=true] - Whether the user is currently active.
 *   When false, version checks are paused to reduce server load.
 *
 * @returns {Object} Version check state object
 * @returns {boolean} returns.updateAvailable - True if a new version is deployed
 * @returns {string} returns.newVersion - The version identifier of the new build
 *
 * @example
 * // Default usage (checks every 30 minutes)
 * const { updateAvailable, newVersion } = useVersionCheck();
 *
 * if (updateAvailable) {
 *   console.log(`New version available: ${newVersion}`);
 * }
 *
 * @example
 * // With user activity detection (pauses when inactive)
 * const { isActive } = useUserActivity(10 * 60 * 1000); // 10 min inactivity
 * const { updateAvailable, newVersion } = useVersionCheck(1800000, isActive);
 *
 * @example
 * // Development mode with frequent checks (every 30 seconds)
 * const checkInterval = process.env.NODE_ENV === 'development' ? 30000 : 1800000;
 * const { updateAvailable, newVersion } = useVersionCheck(checkInterval);
 */
export const useVersionCheck = (checkInterval = DEFAULT_CHECK_INTERVAL, isUserActive = true) => {
  // Get app pause state from AuthContext
  const { isAppPaused } = useAuth();

  // State to track if an update is available
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // State to store the new version identifier
  const [newVersion, setNewVersion] = useState('');

  /**
   * Fetches the deployed version information from the server
   *
   * Makes an HTTP GET request to /version.json to retrieve the current
   * deployed version metadata. This file is generated at build time and
   * contains the git commit hash and build timestamp.
   *
   * Process:
   * 1. Fetch version.json from public directory
   * 2. Parse JSON response
   * 3. Extract version field
   * 4. Compare with current running version
   * 5. Update state if versions differ
   *
   * Error Handling:
   * - Network errors: Logged and ignored (no state update)
   * - Parse errors: Logged and ignored (no state update)
   * - Missing file: Treated as no update available
   * - All errors prevent false-positive notifications
   *
   * Cache Busting:
   * - Adds timestamp query parameter to prevent caching
   * - Ensures fresh version data on each check
   *
   * @async
   * @function
   * @returns {Promise<void>} Resolves when version check completes
   *
   * @throws {Error} Errors are caught and logged, not propagated
   *
   * @example
   * // Called automatically by useEffect
   * await checkForUpdates();
   */
  const checkForUpdates = async () => {
    try {
      // Get the current version from environment variable (set at build time)
      const currentVersion = process.env.REACT_APP_VERSION;

      // If current version is not set, skip the check
      // This can happen in development or if build didn't set the env var
      if (!currentVersion) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('REACT_APP_VERSION not set. Skipping version check.');
        }
        return;
      }

      // Fetch the deployed version with cache busting
      // Add timestamp to prevent browser caching of version.json
      const timestamp = new Date().getTime();
      const response = await fetch(`/version.json?t=${timestamp}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      // Check if request was successful
      if (!response.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Version check failed with status: ${response.status}`);
        }
        return;
      }

      // Parse the JSON response
      const data = await response.json();

      // Extract the deployed version
      const deployedVersion = data.version;

      // If deployed version is not available, skip comparison
      if (!deployedVersion) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Deployed version not found in version.json');
        }
        return;
      }

      // Compare versions - update is available if they differ
      if (deployedVersion !== currentVersion) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Update available!');
          console.log('Current version:', currentVersion);
          console.log('New version:', deployedVersion);
          console.log('Build time:', data.buildTime);
        }

        // Update state to trigger notification
        setUpdateAvailable(true);
        setNewVersion(deployedVersion);
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('Running latest version:', currentVersion);
        }
      }
    } catch (error) {
      // Silently handle errors to prevent disrupting the application
      // Log for debugging purposes but don't show to users
      if (process.env.NODE_ENV === 'development') {
        console.warn('Version check error:', error.message);
      }

      // Note: We don't set updateAvailable to true on error
      // This prevents false-positive update notifications
    }
  };

  /**
   * Effect hook for version checking lifecycle
   *
   * Sets up the periodic version checking mechanism and handles cleanup.
   * Runs the initial check immediately on mount, then sets up an interval
   * to check periodically based on the configured interval.
   *
   * Lifecycle:
   * 1. Component mounts - immediate version check
   * 2. Set up interval for periodic checks
   * 3. Component unmounts - clear interval
   *
   * Cleanup:
   * - Clears interval to prevent memory leaks
   * - Prevents checks after component unmounts
   *
   * Dependencies:
   * - checkInterval: Re-run if interval changes
   * - isUserActive: Re-run when user activity state changes
   *
   * @effect
   */
  useEffect(() => {
    // Check for dev fake banner trigger
    const devFakeBanner = sessionStorage.getItem('devFakeBanner');
    if (devFakeBanner === 'true') {
      // Clear the flag
      sessionStorage.removeItem('devFakeBanner');

      // Set fake version to trigger banner
      setUpdateAvailable(true);
      setNewVersion('dev-test-' + Date.now());

      if (process.env.NODE_ENV === 'development') {
        console.log('Dev mode: Triggering fake update banner');
      }
      return; // Skip normal version checking
    }

    // Don't set up interval if user is inactive or app is paused (tab hidden for 1+ minute)
    if (!isUserActive || isAppPaused) {
      return;
    }

    // Perform initial version check on mount (or when user becomes active again)
    checkForUpdates();

    // Set up periodic version checking
    const intervalId = setInterval(() => {
      checkForUpdates();
    }, checkInterval);

    // Cleanup function - clear interval on unmount or when user becomes inactive
    return () => {
      clearInterval(intervalId);
    };
  }, [checkInterval, isUserActive, isAppPaused]); // Re-run if check interval, activity, or app pause state changes

  // Return version check state
  return {
    updateAvailable,
    newVersion
  };
};
